<template>
  <b-modal
    :model-value="modelValue"
    title="Simpan Berita Acara"
    title-class="text-black font-18"
    body-class="p-4"
    hide-footer
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <!-- ── Loading state saat fetch data validator ───────────────── -->
    <div v-if="loadingValidator" class="text-center py-4">
      <i class="fas fa-spinner fa-spin fa-2x text-secondary"></i>
      <p class="mt-2 text-muted mb-0">Memuat data validator...</p>
    </div>

    <!-- ── Form utama ─────────────────────────────────────────────── -->
    <form v-else @submit.prevent="handleSubmit">
      <!-- ─ SECTION: Data Utama ──────────────────────────────────── -->
      <SectionHeader icon="fa-user" label="Data Utama" />

      <div class="row">
        <div class="col-md-6 form-group mb-3">
          <label>Nama <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nama"
            placeholder="Nama lengkap"
            maxlength="100"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Induk Pegawai <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.nip"
            placeholder="NIP"
            maxlength="100"
          />
        </div>

        <div class="col-md-12 form-group mb-3">
          <label>Alamat Lengkap <span class="text-danger">*</span></label>
          <textarea
            class="form-control"
            rows="2"
            v-model="form.alamat"
            placeholder="Alamat lengkap"
          ></textarea>
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Unit Kerja <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.unit_kerja"
            placeholder="Unit kerja"
            maxlength="100"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Jabatan <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.jabatan"
            placeholder="Jabatan"
            maxlength="100"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Telepon <span class="text-danger">*</span></label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            v-model="form.nomor_telepon"
            placeholder="Nomor telepon"
            maxlength="30"
            @input="form.nomor_telepon = form.nomor_telepon.replace(/\D/g, '')"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nama Bank <span class="text-danger">*</span></label>
          <v-select
            :options="optionBank"
            label="nama_bank"
            :reduce="(opt) => opt.nama_bank"
            v-model="form.nama_bank"
            placeholder="Pilih bank"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Rekening <span class="text-danger">*</span></label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            v-model="form.nomor_rekening"
            placeholder="Nomor rekening"
            @input="form.nomor_rekening = form.nomor_rekening.replace(/\D/g, '')"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Atas Nama Rekening <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="form.atas_nama_rekening"
            placeholder="Atas nama rekening"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>NIK <span class="text-danger">*</span></label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            v-model="form.nik"
            placeholder="Nomor Induk Kependudukan"
            maxlength="20"
            @input="form.nik = form.nik.replace(/\D/g, '')"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>NPWP <span class="text-danger">*</span></label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            v-model="form.npwp"
            placeholder="Nomor Pokok Wajib Pajak"
            maxlength="50"
            @input="form.npwp = form.npwp.replace(/\D/g, '')"
          />
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Upload NDA <span class="text-danger">*</span></label>
          <input
            type="file"
            class="form-control"
            accept=".pdf,.jpg,.jpeg,.png"
            @change="(e) => (form.nda = e.target.files[0] ?? null)"
          />
          <small class="text-muted">
            Format: PDF, JPG, JPEG, PNG. Maks 5MB.
          </small>
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>Nomor Surat</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            v-model="form.nomor_surat"
            placeholder="Nomor Surat (opsional)"
            @input="form.nomor_surat = form.nomor_surat.replace(/\D/g, '')"
          />
        </div>
      </div>

      <!-- ─ SECTION: Upload Dokumen ──────────────────────────────── -->
      <SectionHeader icon="fa-paperclip" label="Upload Dokumen" />

      <div class="row">
        <div class="col-md-6 form-group mb-3">
          <label>
            Upload KTP
            <span class="text-danger" v-if="!form.path_ktp">*</span>
          </label>
          <div class="d-flex gap-2">
            <input
              type="file"
              class="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              @change="(e) => (form.file_ktp = e.target.files[0] ?? null)"
            />
            <a
              v-if="form.path_ktp"
              :href="fileUrl(form.path_ktp)"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-info btn-sm text-nowrap"
            >
              <i class="fa fa-eye me-1"></i> Preview
            </a>
          </div>
          <small class="text-muted">
            Format: PDF, JPG, JPEG, PNG. Maks 5MB.
            <span v-if="form.path_ktp">
              File sudah pernah diupload. Upload file baru untuk mengganti.
            </span>
          </small>
        </div>

        <div class="col-md-6 form-group mb-3">
          <label>
            Upload NPWP
            <span class="text-danger" v-if="!form.path_npwp">*</span>
          </label>
          <div class="d-flex gap-2">
            <input
              type="file"
              class="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              @change="(e) => (form.file_npwp = e.target.files[0] ?? null)"
            />
            <a
              v-if="form.path_npwp"
              :href="fileUrl(form.path_npwp)"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-info btn-sm text-nowrap"
            >
              <i class="fa fa-eye me-1"></i> Preview
            </a>
          </div>
          <small class="text-muted">
            Format: PDF, JPG, JPEG, PNG. Maks 5MB.
            <span v-if="form.path_npwp">
              File sudah pernah diupload. Upload file baru untuk mengganti.
            </span>
          </small>
        </div>
      </div>

      <!-- ─ SECTION: Kritik & Saran ───────────────────────────────── -->
      <SectionHeader icon="fa-comment-dots" label="Kritik & Saran" />

      <div class="form-group mb-4">
        <textarea
          class="form-control"
          rows="4"
          v-model="form.kritik_saran"
          placeholder="Tuliskan kritik atau saran Anda di sini (opsional)"
        ></textarea>
        <small class="text-muted">Tidak wajib diisi.</small>
      </div>

      <!-- ─ SECTION: Tanda Tangan ────────────────────────────────── -->
      <SectionHeader icon="fa-pen-nib" label="Tanda Tangan" required />

      <div class="form-group mb-4">
        <SignaturePad
          ref="signaturePadRef"
          :reset-trigger="signatureResetTrigger"
          :initial-image="form.tanda_tangan"
          @update="(val) => (form.tanda_tangan = val)"
        />
      </div>

      <!-- ─ Action Buttons ───────────────────────────────────────── -->
      <div class="text-end border-top pt-3">
        <button
          type="button"
          class="btn btn-light me-2"
          @click="$emit('update:modelValue', false)"
        >
          Tutup
        </button>
        <button type="submit" class="btn btn-success">
          <i class="fas fa-save me-1"></i> Simpan Berita Acara
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import SignaturePad from "./SignaturePad.vue";
import SectionHeader from "./SectionHeader.vue";
import { useBeritaAcara } from "./useBeritaAcara.js";

export default {
  name: "ModalBeritaAcara",

  components: {
    SignaturePad,
    SectionHeader,
  },

  props: {
    // v-model — kontrol visibilitas modal dari parent
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Row data yang dipilih dari tabel (harus punya idverif_kompetensi_level)
    rowData: {
      type: Object,
      default: null,
    },
  },

  emits: [
    "update:modelValue", // v-model
    "success", // dipancarkan setelah submit berhasil → parent refresh tabel
  ],

  setup() {
    const {
      form,
      loadingValidator,
      resetForm,
      getValidatorData,
      validateForm,
      submitBeritaAcara,
      fileUrl,
    } = useBeritaAcara();

    return {
      form,
      loadingValidator,
      resetForm,
      getValidatorData,
      validateForm,
      submitBeritaAcara,
      fileUrl,
    };
  },

  data() {
    return {
      // Increment untuk trigger reset canvas SignaturePad
      signatureResetTrigger: 0,
      optionBank: [],
    };
  },

  methods: {
    // ── Dipanggil saat modal selesai muncul ──────────────────────
    onModalShown() {
      this.resetForm();
      this.signatureResetTrigger++; // reset canvas tanda tangan
      this.getValidatorData();
      this.getBankOptions();
    },

    // ── Ambil daftar bank untuk dropdown Nama Bank ────────────────
    async getBankOptions() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "master-verifikasi/master-bank",
          {
            params: {
              per_page: 100,
              filter: { active_flag: 1 },
            },
            headers: {
              Authorization: "Bearer " + localStorage.access_token,
            },
          },
        );

        this.optionBank = response.data?.data?.data || [];
      } catch (err) {
        console.error("Gagal memuat daftar bank:", err);
      }
    },

    // ── Dipanggil saat modal ditutup ─────────────────────────────
    onModalHidden() {
      this.resetForm();
      this.signatureResetTrigger++;
    },

    // ── Handler submit form ──────────────────────────────────────
    async handleSubmit() {
      // Cek apakah canvas tanda tangan kosong melalui ref SignaturePad
      const isBlank = this.$refs.signaturePadRef?.isBlank() ?? true;

      const errors = this.validateForm(isBlank);

      if (errors.length) {
        Swal.fire({
          icon: "error",
          title: "Validasi Gagal",
          html: `<ul style="text-align:left;">${errors
            .map((e) => `<li>${e}</li>`)
            .join("")}</ul>`,
        });
        return;
      }

      const confirmed = await Swal.fire({
        title: "Apakah Anda yakin?",
        text: "Berita Acara akan disimpan.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Ya, Lanjutkan!",
        cancelButtonText: "Batalkan",
      });

      if (!confirmed.isConfirmed) return;

      await this.submitBeritaAcara(this.rowData?.idverif_kompetensi_level, {
        onSuccess: () => {
          this.$emit("update:modelValue", false);
          this.$emit("success"); // parent akan reload tabel
        },
      });
    },
  },
};
</script>
